import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListChecks, Loader2, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { listMyAttempts, startTest, testStatusClass, testStatusLabel } from "@/lib/grading";

/** Shown inside StudentCourseView's chapter detail when canView. */
export default function TestPanel({ courseId, chapter, canView }) {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    return listMyAttempts()
      .then((all) =>
        setAttempts(all.filter((a) => a.chapter_id === chapter?.id))
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canView && chapter) refresh();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, chapter, canView]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const resp = await startTest(courseId, chapter.id);
      navigate(`/student/test-attempts/${resp.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start test");
    } finally {
      setStarting(false);
    }
  };

  if (!canView) {
    return (
      <div className="py-10 text-center text-stone-500 text-sm">
        Complete your payment to take this chapter's test.
      </div>
    );
  }

  const mcqCount = chapter?.mcq?.length || 0;
  const subjCount = chapter?.subjective?.length || 0;
  const duration = chapter?.test_duration_minutes || 30;

  return (
    <div className="space-y-4" data-testid="test-panel">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-amber-600" />
        <span className="font-medium text-sm">
          {chapter.title} — Test ({duration} min)
        </span>
      </div>
      <div className="text-xs text-stone-500">
        {mcqCount} MCQs · {subjCount} subjective
      </div>

      {mcqCount === 0 && subjCount === 0 ? (
        <p className="text-sm text-stone-500">
          No test configured for this chapter.
        </p>
      ) : (
        <>
          <Button
            onClick={handleStart}
            disabled={starting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="start-test-button"
          >
            {starting ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="mr-1 h-4 w-4" />
            )}
            Start test
          </Button>
          <p className="text-xs text-stone-500">
            Multiple attempts allowed. Your most recent attempt is graded.
          </p>
        </>
      )}

      <div className="pt-4 border-t border-stone-200 space-y-2">
        <div className="text-xs uppercase text-stone-500 tracking-wide">
          Your attempts ({attempts.length})
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
        ) : attempts.length === 0 ? (
          <p className="text-xs text-stone-500">No attempts yet.</p>
        ) : (
          <ul className="space-y-2">
            {attempts.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between border border-stone-200 rounded px-3 py-2 text-sm"
                data-testid={`attempt-row-${a.id}`}
              >
                <div>
                  <div className="font-medium">
                    {a.mcq_score}/{a.mcq_total} MCQ
                    {a.subjective_total != null && (
                      <span className="text-stone-500">
                        {" · "}
                        {a.subjective_score ?? "—"}/{a.subjective_total} subj
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500">
                    {new Date(a.started_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={testStatusClass(a.status)}>
                    {testStatusLabel(a.status)}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      navigate(`/student/test-attempts/${a.id}`)
                    }
                    data-testid={`view-attempt-${a.id}`}
                  >
                    View
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
